import React from "react";
import { format } from "date-fns";
import { CommandXFooter } from "./CommandXFooter";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(n || 0);

const num = (n: number, digits = 2) =>
  new Intl.NumberFormat("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(n || 0);

export interface ContractSovPrintLine {
  id: string;
  line_number: number;
  description: string;
  unit: string;
  quantity: number;
  labor_unit_price: number;
  material_unit_price: number;
  unit_price: number;
  scheduled_value: number;
  parent_group?: string | null;
  is_passthrough?: boolean;
}

export interface ContractSovPrintGroup {
  groupName: string | null;
  lines: ContractSovPrintLine[];
}

export interface ContractSovPrintData {
  // Company / branding
  company_name?: string;
  company_logo_url?: string | null;

  // Project
  project_name: string;
  project_number?: string | null;
  customer_name?: string;
  city?: string | null;
  state?: string | null;
  billing_mode?: string | null;

  // Contract
  contract_name: string;
  contract_status: string; // 'draft' | 'active' | 'closed' | 'cancelled'
  contract_description?: string | null;
  original_value: number;
  revised_value: number;
  finalized_at?: string | null;
  created_at: string;

  // SOV
  groups: ContractSovPrintGroup[];
}

const STATUS_STYLES: Record<
  string,
  { label: string; bg: string; color: string; border: string }
> = {
  draft: {
    label: "DRAFT",
    bg: "#FEF3C7",
    color: "#92400E",
    border: "#F59E0B",
  },
  active: {
    label: "FINALIZED",
    bg: "#D1FAE5",
    color: "#065F46",
    border: "#059669",
  },
  closed: {
    label: "CLOSED",
    bg: "#E5E7EB",
    color: "#374151",
    border: "#9CA3AF",
  },
  cancelled: {
    label: "CANCELLED",
    bg: "#FEE2E2",
    color: "#991B1B",
    border: "#DC2626",
  },
};

export const ContractSovPrintTemplate = React.forwardRef<
  HTMLDivElement,
  { data: ContractSovPrintData }
>(({ data }, ref) => {
  const location = [data.city, data.state].filter(Boolean).join(", ");
  const isDraft = data.contract_status === "draft";
  const status = STATUS_STYLES[data.contract_status] ?? STATUS_STYLES.draft;

  const grandTotal = data.groups.reduce(
    (sum, g) => sum + g.lines.reduce((s, l) => s + Number(l.scheduled_value || 0), 0),
    0,
  );
  const totalLines = data.groups.reduce((s, g) => s + g.lines.length, 0);

  // Numeric column style — tabular numerals so decimals align across rows.
  const numCell: React.CSSProperties = {
    textAlign: "right",
    fontFamily:
      "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
    padding: "6px 8px",
    borderBottom: "1px solid #f1f5f9",
    fontSize: 11,
  };
  const textCell: React.CSSProperties = {
    padding: "6px 8px",
    borderBottom: "1px solid #f1f5f9",
    fontSize: 11,
    verticalAlign: "top",
  };
  const headCell: React.CSSProperties = {
    padding: "8px",
    borderBottom: "2px solid #111",
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: "#374151",
    background: "#F9FAFB",
  };

  return (
    <div
      ref={ref}
      className="print-template"
      style={{
        padding: "32px",
        maxWidth: "8.5in",
        margin: "0 auto",
        fontFamily:
          "ui-sans-serif, system-ui, -apple-system, Segoe UI, sans-serif",
        color: "#111",
        fontSize: 12,
        position: "relative",
      }}
    >
      {/* Draft watermark — diagonal, light, behind content */}
      {isDraft && (
        <div
          aria-hidden
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
            zIndex: 0,
          }}
        >
          <span
            style={{
              fontSize: 160,
              fontWeight: 800,
              color: "#F59E0B",
              opacity: 0.08,
              transform: "rotate(-30deg)",
              letterSpacing: "0.1em",
            }}
          >
            DRAFT
          </span>
        </div>
      )}

      <div style={{ position: "relative", zIndex: 1 }}>
        {/* Header band */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            borderBottom: "2px solid #111",
            paddingBottom: 12,
            marginBottom: 16,
          }}
        >
          <div>
            {data.company_logo_url && (
              <img
                src={data.company_logo_url}
                alt={data.company_name ?? "Company"}
                style={{
                  maxHeight: 40,
                  objectFit: "contain",
                  marginBottom: 4,
                }}
              />
            )}
            <h1
              style={{
                fontSize: 22,
                fontWeight: 800,
                letterSpacing: "-0.01em",
                margin: 0,
              }}
            >
              CONTRACT / SCHEDULE OF VALUES
            </h1>
            {data.company_name && (
              <p
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#374151",
                  margin: "4px 0 0",
                }}
              >
                {data.company_name}
              </p>
            )}
          </div>
          <div style={{ textAlign: "right" }}>
            {data.project_number && (
              <p
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  fontFamily:
                    "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
                  margin: 0,
                }}
              >
                {data.project_number}
              </p>
            )}
            <span
              style={{
                display: "inline-block",
                marginTop: 6,
                padding: "4px 10px",
                borderRadius: 4,
                background: status.bg,
                color: status.color,
                border: `1px solid ${status.border}`,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.06em",
              }}
            >
              {status.label}
            </span>
          </div>
        </div>

        {/* Project / Contract metadata grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 16,
            marginBottom: 20,
          }}
        >
          <MetaField label="Project" value={data.project_name} />
          <MetaField label="Customer" value={data.customer_name || "—"} />
          <MetaField label="Location" value={location || "—"} />
          <MetaField label="Contract" value={data.contract_name} />
          <MetaField
            label="Billing Mode"
            value={(data.billing_mode || "—").replace(/_/g, " ")}
          />
          <MetaField
            label="Created"
            value={format(new Date(data.created_at), "MMM d, yyyy")}
          />
          <MetaField
            label="Original Value"
            value={fmt(Number(data.original_value))}
            mono
          />
          <MetaField
            label="Revised Value"
            value={fmt(Number(data.revised_value))}
            mono
          />
          <MetaField
            label={isDraft ? "Status" : "Finalized"}
            value={
              data.finalized_at
                ? format(new Date(data.finalized_at), "MMM d, yyyy")
                : isDraft
                  ? "Pending finalization"
                  : "—"
            }
          />
        </div>

        {data.contract_description && (
          <div
            style={{
              marginBottom: 16,
              padding: 10,
              background: "#F9FAFB",
              border: "1px solid #E5E7EB",
              borderRadius: 4,
              fontSize: 11,
              color: "#374151",
            }}
          >
            {data.contract_description}
          </div>
        )}

        {/* SOV Table */}
        <h2
          style={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            color: "#6B7280",
            margin: "20px 0 8px",
          }}
        >
          Schedule of Values — {totalLines} {totalLines === 1 ? "line" : "lines"}
        </h2>

        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            tableLayout: "fixed",
          }}
        >
          <colgroup>
            <col style={{ width: "5%" }} />
            <col style={{ width: "32%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "7%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "14%" }} />
          </colgroup>
          <thead style={{ display: "table-header-group" }}>
            <tr>
              <th style={{ ...headCell, textAlign: "left" }}>#</th>
              <th style={{ ...headCell, textAlign: "left" }}>Description</th>
              <th style={{ ...headCell, textAlign: "right" }}>Qty</th>
              <th style={{ ...headCell, textAlign: "left" }}>Unit</th>
              <th style={{ ...headCell, textAlign: "right" }}>Labor Unit $</th>
              <th style={{ ...headCell, textAlign: "right" }}>
                Material Unit $
              </th>
              <th style={{ ...headCell, textAlign: "right" }}>Unit $</th>
              <th style={{ ...headCell, textAlign: "right" }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {data.groups.map((group, gi) => {
              const groupSubtotal = group.lines.reduce(
                (s, l) => s + Number(l.scheduled_value || 0),
                0,
              );
              return (
                <React.Fragment key={`g-${gi}`}>
                  {group.groupName && (
                    <tr style={{ pageBreakInside: "avoid" }}>
                      <td
                        colSpan={8}
                        style={{
                          padding: "10px 8px 4px",
                          fontSize: 11,
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                          color: "#111",
                          background: "#F3F4F6",
                          borderTop: "1px solid #E5E7EB",
                        }}
                      >
                        {group.groupName}
                      </td>
                    </tr>
                  )}
                  {group.lines.map((line) => (
                    <tr
                      key={line.id}
                      style={{ pageBreakInside: "avoid" }}
                    >
                      <td style={{ ...textCell, color: "#6B7280" }}>
                        {line.line_number}
                      </td>
                      <td style={textCell}>
                        {line.description}
                        {line.is_passthrough && (
                          <span
                            style={{
                              marginLeft: 6,
                              padding: "1px 5px",
                              fontSize: 9,
                              border: "1px solid #9CA3AF",
                              borderRadius: 3,
                              color: "#6B7280",
                              textTransform: "uppercase",
                              letterSpacing: "0.04em",
                            }}
                          >
                            Pass-through
                          </span>
                        )}
                      </td>
                      <td style={numCell}>{num(Number(line.quantity), 2)}</td>
                      <td
                        style={{
                          ...textCell,
                          color: "#6B7280",
                          fontSize: 10,
                        }}
                      >
                        {line.unit}
                      </td>
                      <td style={numCell}>
                        {fmt(Number(line.labor_unit_price))}
                      </td>
                      <td style={numCell}>
                        {fmt(Number(line.material_unit_price))}
                      </td>
                      <td style={numCell}>{fmt(Number(line.unit_price))}</td>
                      <td style={{ ...numCell, fontWeight: 600 }}>
                        {fmt(Number(line.scheduled_value))}
                      </td>
                    </tr>
                  ))}
                  {group.groupName && (
                    <tr style={{ pageBreakInside: "avoid" }}>
                      <td
                        colSpan={7}
                        style={{
                          padding: "6px 8px",
                          fontSize: 10,
                          textAlign: "right",
                          fontWeight: 600,
                          color: "#6B7280",
                          background: "#FAFAFA",
                          borderBottom: "1px solid #E5E7EB",
                        }}
                      >
                        Subtotal — {group.groupName}
                      </td>
                      <td
                        style={{
                          ...numCell,
                          fontWeight: 700,
                          background: "#FAFAFA",
                          borderBottom: "1px solid #E5E7EB",
                        }}
                      >
                        {fmt(groupSubtotal)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {data.groups.length === 0 && (
              <tr>
                <td
                  colSpan={8}
                  style={{
                    padding: 20,
                    textAlign: "center",
                    color: "#9CA3AF",
                    fontSize: 11,
                  }}
                >
                  No SOV lines.
                </td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr>
              <td
                colSpan={7}
                style={{
                  padding: "10px 8px",
                  textAlign: "right",
                  fontSize: 12,
                  fontWeight: 700,
                  borderTop: "2px solid #111",
                }}
              >
                CONTRACT TOTAL
              </td>
              <td
                style={{
                  ...numCell,
                  fontSize: 13,
                  fontWeight: 800,
                  borderTop: "2px solid #111",
                  borderBottom: "none",
                }}
              >
                {fmt(grandTotal)}
              </td>
            </tr>
          </tfoot>
        </table>

        <CommandXFooter />
      </div>
    </div>
  );
});

ContractSovPrintTemplate.displayName = "ContractSovPrintTemplate";

const MetaField = ({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) => (
  <div>
    <p
      style={{
        fontSize: 9,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        fontWeight: 600,
        color: "#9CA3AF",
        margin: "0 0 2px",
      }}
    >
      {label}
    </p>
    <p
      style={{
        fontSize: 12,
        fontWeight: 500,
        margin: 0,
        fontFamily: mono
          ? "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace"
          : undefined,
        textTransform: mono ? "none" : "capitalize",
      }}
    >
      {value}
    </p>
  </div>
);
